import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(formData.name, formData.email, formData.password);
        toast.success('Account created successfully!');
      } else {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at top left, #2e1065 0%, #1e1b4b 40%, #0f0a28 100%)' }}
    >
      {/* Decorative blobs */}
      <div
        className="absolute w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)',
          top: '-150px', left: '-150px',
        }}
      />
      <div
        className="absolute w-[350px] h-[350px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(109,40,217,0.15) 0%, transparent 70%)',
          bottom: '-80px', right: '-80px',
        }}
      />
      <div
        className="absolute w-[200px] h-[200px] rounded-full pointer-events-none animate-float"
        style={{
          background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)',
          top: '30%', right: '10%',
        }}
      />

      <div className="w-full max-w-md relative z-10 animate-fade-up">
        {/* Brand mark */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center rounded-2xl mb-5 bg-white p-3 shadow-glow-purple">
            <img
              src={`${process.env.PUBLIC_URL}/dtx-distributor-logo.png`}
              alt="DTX Distributor"
              className="h-14 w-auto max-w-[200px] object-contain"
            />
          </div>
          <h1 className="text-3xl font-heading font-bold text-white tracking-tight">DTX Distributor</h1>
          <p className="text-purple-300/80 mt-1.5 text-sm">Distributor Management Platform</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-white/10">
          <div className="mb-6">
            <h2 className="text-xl font-heading font-bold text-slate-900">
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {isRegister
                ? 'Fill in your details to get started'
                : 'Sign in to your admin panel'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="animate-fade-up">
                <Label htmlFor="name" className="form-label">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required={isRegister}
                  data-testid="name-input"
                  className="h-11 rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="form-label">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="admin@diternox.com"
                required
                data-testid="email-input"
                className="h-11 rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400"
              />
            </div>

            <div>
              <Label htmlFor="password" className="form-label">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                  className="h-11 pr-11 rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4.5 h-4.5" style={{width:'18px',height:'18px'}} /> : <Eye className="w-4.5 h-4.5" style={{width:'18px',height:'18px'}} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-btn"
              className="w-full h-11 rounded-xl text-sm font-semibold gap-2 mt-2 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isRegister ? 'Creating Account…' : 'Signing In…'}
                </>
              ) : (
                <>
                  {isRegister ? 'Create Account' : 'Sign In'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-5 text-center">
            <p className="text-slate-500 text-sm">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}
              {' '}
              <button
                type="button"
                onClick={() => setIsRegister(!isRegister)}
                className="text-purple-700 hover:text-purple-900 font-semibold transition-colors"
                data-testid="toggle-auth-mode-btn"
              >
                {isRegister ? 'Sign In' : 'Create One'}
              </button>
            </p>
          </div>
        </div>

        <p className="text-center text-purple-300/40 text-xs mt-6">
          DTX Distributor System · v1.0
        </p>
      </div>
    </div>
  );
};

export default Login;
